// O relatório que a família mostra ao médico — e o que a família lê sozinha.
//
// Nasceu de uma cena concreta: o médico olha a ficha no celular e diz "me manda
// um relatório do que vocês têm aí".
//
// Duas versões do mesmo conteúdo, e a diferença não é de dados — é de leitor.
// A do médico é densa e só com fato. A da família traz o que cada medida
// significa e as doc__perguntas para levar na visita.
//
// Vira PDF pelo "imprimir" do próprio navegador, que no Android já oferece
// "salvar como PDF" e compartilhar no WhatsApp. Sem biblioteca, sem servidor
// gerando arquivo: o CSS de impressão faz o trabalho.
import { useCallback, useEffect, useState } from 'react';
import {
  ROTULO_GRUPO, diaDoInstante, formatarData, formatarDataHora, formatarDiaMes, formatarNumero, podeEscrever,
  PERGUNTAS_DO_DIA, ROTULO_RESPOSTA, MARCA_RESPOSTA, sonoEmTexto,
  type Perfil,
} from '@medicov1/shared';
import { chamar } from '../api';
import type { Periodo, Versao, RelatorioDTO } from './relatorioTipos';
import { gerarPdf } from './documentoPdf';
import { gerarPdfImagem } from './documentoPdfImagem';
import { FolhaImagem, type DocumentoDeImagemDTO } from './RelatorioImagem';
import { gerarPdfEspecial } from './documentoPdfEspecial';
import { gerarPdfLaboratorio } from './documentoPdfLaboratorio';
import { FolhaLaboratorio, type FolhaDeLaboratorioDTO } from './RelatorioLaboratorio';
import { HistoricoDoMarcador } from './HistoricoDoMarcador';
import { FolhaEspecial, type RelatorioEspecialDTO, type ResumoEspecialDTO } from './RelatorioEspecial';
import { FolhaRelatos } from './RelatorioRelatos';
import { NotaFormatada } from './NotaFormatada';
import { agruparEspeciais, grupoDoEspecial } from './relatorioEspecialTipos';
import { EscolhaDoEspecial } from './EscolhaDoEspecial';
import { identificar } from './identificacao';
import { faixaDoSinal, geometriaDoSinal, sinaisComGrafico } from './graficoDeSinais';
import { gerarPdfRelatos } from './documentoPdfRelatos';
import { PERIODOS_DOS_RELATOS, type PeriodoDosRelatos, type RelatosDTO } from './relatorioRelatosTipos';
import './relatorio.css';

const ROTULO_SINAL: Record<string, string> = {
  glicemia: 'Glicemia', pa_sistolica: 'Pressão sistólica', pa_diastolica: 'Pressão diastólica',
  frequencia_cardiaca: 'Frequência cardíaca', saturacao: 'Saturação de oxigênio',
  temperatura: 'Temperatura', frequencia_respiratoria: 'Frequência respiratória',
  diurese: 'Diurese', evacuacoes: 'Evacuações', dieta_enteral: 'Dieta enteral',
  balanco_hidrico: 'Balanço hídrico', peso: 'Peso',
};

const PERIODOS: { chave: Periodo; rotulo: string }[] = [
  // Passar o turno vem primeiro porque é o que mais se usa: é o documento que
  // quem sai do quarto entrega a quem entra.
  { chave: 'turno', rotulo: 'Passar o turno' },
  { chave: 'diario', rotulo: 'Diário' },
  { chave: 'semanal', rotulo: 'Semanal' },
  { chave: 'quinzenal', rotulo: 'Quinzenal' },
  { chave: 'geral', rotulo: 'Geral' },
];

// Data e hora vem do modulo compartilhado, fixado em Brasilia. Nada aqui usa o
// relogio do aparelho: o relatorio tem que dizer o mesmo dia no celular de quem
// esta em Brasilia, no de quem esta em outro estado e no PDF que sai dos dois.
const dia = formatarData;
const diaHora = formatarDataHora;
const numero = (n: number) => formatarNumero(n, 1);

const SETA = { subiu: '↑', caiu: '↓', estavel: '→' } as const;

/** Faixa de referência sem perder casa decimal: 7,35 não pode virar 7,4. */
const faixa = (n: number) => formatarNumero(n, 2);

interface Props {
  pacienteId: string;
  perfil: Perfil;
  aoVoltar: () => void;
}

export function Relatorio({ pacienteId, perfil, aoVoltar }: Props) {
  // Riscar uma pergunta da lista é anotar o que a equipe respondeu: quem pode
  // anotar, pode. Quem só lê continua lendo as perguntas.
  const podeResponder = podeEscrever(perfil);
  const [respondendo, setRespondendo] = useState<string | null>(null);
  const [textoResposta, setTextoResposta] = useState('');
  const [quemRespondeu, setQuemRespondeu] = useState('');
  const [salvando, setSalvando] = useState(false);
  // Abre no diário: quem abre o relatório quer saber do dia que está vivendo.
  // O histórico está a um toque, mas não é o que se procura às seis da manhã.
  const [periodo, setPeriodo] = useState<Periodo>('diario');
  /**
   * Dois documentos moram nesta tela.
   *
   * O acompanhamento tem período e versão; o de exames de imagem não tem
   * nenhum dos dois — é a internação inteira, para quem quiser ler, e o que
   * ele explica não muda conforme quem lê. Por isso os controles somem quando
   * ele está escolhido: botão que não faz nada é botão que confunde.
   */
  const [tipo, setTipo] = useState<'acompanhamento' | 'relatos' | 'imagem' | 'laboratorio' | 'especiais'>('acompanhamento');
  /**
   * Os relatos da família, em documento próprio (25/09): saíram do
   * acompanhamento, onde empurravam os exames para longe de quem lê. Período
   * separado do acompanhamento porque aqui não existe "passar o turno" — o
   * turno já traz os relatos dele.
   */
  const [relatos, setRelatos] = useState<RelatosDTO | null>(null);
  const [periodoRelatos, setPeriodoRelatos] = useState<PeriodoDosRelatos>('semanal');
  /** A folha de laboratório da cabeceira: números do banco, leitura escrita à parte. */
  const [laboratorio, setLaboratorio] = useState<FolhaDeLaboratorioDTO | null>(null);
  /**
   * "Exames do dia" (pedido do Ilton, 01/10): null = o último valor de cada
   * exame (a folha de sempre); uma data = só a coleta daquele dia, cada exame
   * comparado com o anterior a ele, e o toque abre a história inteira.
   */
  const [diaLab, setDiaLab] = useState<string | null>(null);
  const [imagem, setImagem] = useState<DocumentoDeImagemDTO | null>(null);
  /**
   * Os relatórios especiais são vários: a lista escolhe, o documento aberto
   * é um. Abre-se o mais recente, que é o que quem entra aqui veio ler.
   */
  const [especiais, setEspeciais] = useState<ResumoEspecialDTO[] | null>(null);
  const [especialId, setEspecialId] = useState<string | null>(null);
  /** O exame tocado na folha de laboratório: abre a história dele (27/09). */
  const [marcadorAberto, setMarcadorAberto] = useState<string | null>(null);
  const [especial, setEspecial] = useState<RelatorioEspecialDTO | null>(null);
  const [versao, setVersao] = useState<Versao>('familia');
  const [dados, setDados] = useState<RelatorioDTO | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState(false);

  /**
   * Monta o PDF e baixa. Sem diálogo de impressão no meio.
   *
   * A biblioteca e as fontes chegam por `import()` na hora do clique — quase
   * 2 MB que ninguém precisa baixar para ver o monitor do quarto. Por isso o
   * botão avisa que está trabalhando: no 4G do hospital isso leva segundos.
   */
  const baixarPdf = useCallback(async () => {
    if (tipo === 'relatos') {
      if (!relatos) return;
      setGerando(true);
      try { await gerarPdfRelatos(relatos); }
      catch (e) { setErro(e instanceof Error ? e.message : 'não consegui gerar o PDF'); }
      finally { setGerando(false); }
      return;
    }
    if (tipo === 'laboratorio') {
      if (!laboratorio) return;
      setGerando(true);
      try { await gerarPdfLaboratorio(laboratorio); }
      catch (e) { setErro(e instanceof Error ? e.message : 'não consegui gerar o PDF'); }
      finally { setGerando(false); }
      return;
    }
    if (tipo === 'especiais') {
      if (!especial) return;
      setGerando(true);
      try { await gerarPdfEspecial(especial); }
      catch (e) { setErro(e instanceof Error ? e.message : 'não consegui gerar o PDF'); }
      finally { setGerando(false); }
      return;
    }
    if (tipo === 'imagem') {
      if (!imagem) return;
      setGerando(true);
      try { await gerarPdfImagem(imagem); }
      catch (e) { setErro(e instanceof Error ? e.message : 'não consegui gerar o PDF'); }
      finally { setGerando(false); }
      return;
    }
    if (!dados) return;
    setGerando(true);
    try {
      await gerarPdf(dados, periodo, versao);
    } catch (e) {
      setErro(e instanceof Error ? `não consegui gerar o PDF: ${e.message}` : 'não consegui gerar o PDF');
    } finally {
      setGerando(false);
    }
  }, [dados, periodo, versao, tipo, imagem, especial, laboratorio, relatos]);

  const carregarRelatos = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setRelatos(await chamar<RelatosDTO>(`/relatorio/relatos?pacienteId=${pacienteId}&periodo=${periodoRelatos}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui montar os relatos');
    } finally {
      setCarregando(false);
    }
  }, [pacienteId, periodoRelatos]);

  const carregarLaboratorio = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setLaboratorio(await chamar<FolhaDeLaboratorioDTO>(
        `/relatorio/laboratorio?pacienteId=${pacienteId}${diaLab ? `&dia=${diaLab}` : ''}`,
      ));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui montar a folha de laboratório');
    } finally {
      setCarregando(false);
    }
  }, [pacienteId, diaLab]);

  const carregarEspeciais = useCallback(async () => {
    setErro('');
    try {
      const lista = await chamar<ResumoEspecialDTO[]>(`/relatorios-especiais?pacienteId=${pacienteId}`);
      setEspeciais(lista);
      // Sem escolha ainda, abre o primeiro do primeiro grupo — o que se entrega.
      setEspecialId((atual) => (atual && lista.some((x) => x.id === atual) ? atual : agruparEspeciais(lista)[0]?.itens[0]?.id ?? null));
      if (lista.length === 0) { setEspecial(null); setCarregando(false); }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui listar os relatórios');
      setCarregando(false);
    }
  }, [pacienteId]);

  const carregarEspecial = useCallback(async (id: string) => {
    setCarregando(true);
    setErro('');
    try {
      setEspecial(await chamar<RelatorioEspecialDTO>(`/relatorios-especiais/${id}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui abrir o relatório');
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarImagem = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setImagem(await chamar<DocumentoDeImagemDTO>(`/relatorio/imagem?pacienteId=${pacienteId}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui montar o relatório');
    } finally {
      setCarregando(false);
    }
  }, [pacienteId]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await chamar<RelatorioDTO>(
        `/relatorio?pacienteId=${pacienteId}&periodo=${periodo}&versao=${versao}`,
      ));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui montar o relatório');
    } finally {
      setCarregando(false);
    }
  }, [pacienteId, periodo, versao]);

  useEffect(() => {
    if (tipo === 'imagem') void carregarImagem();
    else if (tipo === 'relatos') void carregarRelatos();
    else if (tipo === 'laboratorio') void carregarLaboratorio();
    else if (tipo === 'especiais') void carregarEspeciais();
    else void carregar();
  }, [tipo, carregar, carregarImagem, carregarLaboratorio, carregarEspeciais, carregarRelatos]);

  useEffect(() => {
    if (tipo === 'especiais' && especialId) void carregarEspecial(especialId);
  }, [tipo, especialId, carregarEspecial]);

  /**
   * Risca a pergunta da lista, guardando o que responderam.
   *
   * Escrever a resposta é opcional — às vezes só se quer tirar da lista. Mas
   * quando escrita, é a parte que mais vale: a pergunta some e a explicação
   * fica, para quem entrar no quarto depois não perguntar de novo.
   */
  const marcarRespondida = useCallback(async (q: { chave: string; sobre: string; texto: string }) => {
    setSalvando(true);
    try {
      await chamar('/perguntas-respondidas', {
        metodo: 'POST',
        corpo: {
          pacienteId,
          chave: q.chave,
          pergunta: `${q.sobre} — ${q.texto}`,
          resposta: textoResposta.trim() || null,
          quemRespondeu: quemRespondeu.trim() || null,
        },
      });
      setRespondendo(null);
      setTextoResposta('');
      setQuemRespondeu('');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui guardar a resposta');
    } finally {
      setSalvando(false);
    }
  }, [pacienteId, textoResposta, quemRespondeu, carregar]);

  const paraFamilia = versao === 'familia';
  const p = dados?.paciente;

  return (
    <main className="rel">
      {/* Os controles somem na impressão: no PDF eles seriam lixo. */}
      <header className="rel__controles">
        <div className="rel__grupo rel__tipo">
          <span className="rel__rotulo">Documento</span>
          <div className="rel__botoes">
            <button className={`rel__botao${tipo === 'acompanhamento' ? ' rel__botao--on' : ''}`}
              onClick={() => setTipo('acompanhamento')}>Acompanhamento</button>
            <button className={`rel__botao${tipo === 'relatos' ? ' rel__botao--on' : ''}`}
              onClick={() => setTipo('relatos')}>Relatos</button>
            <button className={`rel__botao${tipo === 'imagem' ? ' rel__botao--on' : ''}`}
              onClick={() => setTipo('imagem')}>Exames de imagem</button>
            <button className={`rel__botao${tipo === 'laboratorio' ? ' rel__botao--on' : ''}`}
              onClick={() => setTipo('laboratorio')}>Laboratório</button>
            <button className={`rel__botao${tipo === 'especiais' ? ' rel__botao--on' : ''}`}
              onClick={() => setTipo('especiais')}>Especiais</button>
          </div>
        </div>

        {/* Um menu só, com os grupos dentro (30/09): a lista de botões poluía a aba. */}
        {tipo === 'especiais' && especiais && especiais.length > 0 && (
          <EscolhaDoEspecial especiais={especiais} especialId={especialId} aoEscolher={setEspecialId} />
        )}

        {/* Exames do dia (01/10): um botão por coleta, do mais recente. A
            fileira rola para o lado — são dezenas de coletas na internação. */}
        {tipo === 'laboratorio' && laboratorio && laboratorio.coletas && laboratorio.coletas.length > 0 && (
          <div className="rel__grupo">
            <span className="rel__rotulo">Exames do dia</span>
            <div className="rel__botoes rel__botoes--rolar">
              <button className={`rel__botao${!diaLab ? ' rel__botao--on' : ''}`}
                onClick={() => setDiaLab(null)}>Último de cada</button>
              {laboratorio.coletas.map((c) => (
                <button key={c.dia}
                  className={`rel__botao${diaLab === c.dia ? ' rel__botao--on' : ''}`}
                  onClick={() => setDiaLab(c.dia)}>
                  {formatarDiaMes(new Date(`${c.dia}T12:00:00`))} · {c.quantidade}
                </button>
              ))}
            </div>
          </div>
        )}

        {tipo === 'relatos' && (
          <div className="rel__grupo">
            <span className="rel__rotulo">Período</span>
            <div className="rel__botoes">
              {PERIODOS_DOS_RELATOS.map((x) => (
                <button key={x.chave}
                  className={`rel__botao${periodoRelatos === x.chave ? ' rel__botao--on' : ''}`}
                  onClick={() => setPeriodoRelatos(x.chave)}>{x.rotulo}</button>
              ))}
            </div>
          </div>
        )}

        {tipo === 'acompanhamento' && (<>
        <div className="rel__grupo">
          <span className="rel__rotulo">Período</span>
          <div className="rel__botoes">
            {PERIODOS.map((x) => (
              <button key={x.chave}
                className={`rel__botao${periodo === x.chave ? ' rel__botao--on' : ''}`}
                onClick={() => setPeriodo(x.chave)}>{x.rotulo}</button>
            ))}
          </div>
        </div>

        <div className="rel__grupo">
          <span className="rel__rotulo">Para quem</span>
          <div className="rel__botoes">
            <button className={`rel__botao${paraFamilia ? ' rel__botao--on' : ''}`}
              onClick={() => setVersao('familia')}>Família</button>
            <button className={`rel__botao${!paraFamilia ? ' rel__botao--on' : ''}`}
              onClick={() => setVersao('medico')}>Médico</button>
          </div>
        </div>
        </>)}

        <div className="rel__acoes">
          <button className="botao" onClick={() => void baixarPdf()}
            disabled={(tipo === 'imagem' ? !imagem : tipo === 'relatos' ? !relatos : tipo === 'laboratorio' ? !laboratorio : tipo === 'especiais' ? !especial : !dados) || gerando}>
            {gerando ? 'gerando…' : 'Baixar PDF'}
          </button>
          <button className="botao botao--voltar" onClick={aoVoltar}>Voltar</button>
        </div>
      </header>

      {/* O relatório é comprido: quem rolou até o fim não deveria ter que subir
          tudo de novo para gerar o PDF. Este botão acompanha a rolagem. */}
      {(tipo === 'imagem' ? imagem : tipo === 'relatos' ? relatos : tipo === 'laboratorio' ? laboratorio : tipo === 'especiais' ? especial : dados) && (
        <button className="rel__flutuante" onClick={() => void baixarPdf()} disabled={gerando}>
          {gerando ? 'gerando…' : 'Baixar PDF'}
        </button>
      )}

      <p className="rel__dica">
        <b>Baixar PDF</b> salva o arquivo pronto no celular — é só abrir o
        WhatsApp e anexar. O que está na tela é o mesmo que sai no arquivo.
      </p>

      {erro && <p className="aviso-erro">{erro}</p>}
      {carregando && <p className="rel__carregando">montando o relatório…</p>}

      {tipo === 'imagem' && imagem && <FolhaImagem dados={imagem} />}
      {tipo === 'relatos' && relatos && <FolhaRelatos dados={relatos} />}
      {tipo === 'laboratorio' && laboratorio && <FolhaLaboratorio dados={laboratorio} aoAbrirMarcador={setMarcadorAberto} />}
      {marcadorAberto && (
        <HistoricoDoMarcador pacienteId={pacienteId} chave={marcadorAberto} aoFechar={() => setMarcadorAberto(null)} />
      )}

      {tipo === 'especiais' && especiais && especiais.length === 0 && !carregando && (
        <p className="rel__dica">Nenhum relatório especial ainda. Eles nascem de uma pergunta da família e ficam aqui, datados.</p>
      )}
      {/* Aviso só na tela (some na impressão): quem abre para imprimir vê antes. */}
      {tipo === 'especiais' && especial && grupoDoEspecial(especial) === 'interno' && (
        <p className="esp-aviso">Registro só da família — não entregar à equipe.</p>
      )}
      {tipo === 'especiais' && especial && <FolhaEspecial dados={especial} />}

      {tipo === 'acompanhamento' && dados && p && (
        <Folha
          dados={dados}
          periodo={periodo}
          paraFamilia={paraFamilia}
          podeResponder={podeResponder}
          respondendo={respondendo}
          textoResposta={textoResposta}
          quemRespondeu={quemRespondeu}
          salvando={salvando}
          aoResponder={setRespondendo}
          aoTrocarTexto={setTextoResposta}
          aoTrocarQuem={setQuemRespondeu}
          aoGuardar={(q) => void marcarRespondida(q)}
        />
      )}
    </main>
  );
}

/**
 * O gráfico dia a dia dos sinais: um traço do menor ao maior do dia, um ponto na
 * média, a faixa usual em verde. A tabela diz o extremo do período; o gráfico
 * diz QUANDO — se a pressão baixa foi um dia ou a semana inteira.
 */
function SinaisDiaADia({ sinais }: { sinais: RelatorioDTO['sinais'] }) {
  const comGrafico = sinaisComGrafico(sinais);
  if (comGrafico.length === 0) return null;
  const L = 300;
  const H = 40;
  return (
    <div className="sinais-dia">
      <p className="doc__legenda">
        <b>Dia a dia</b> — cada traço vai do menor ao maior valor medido no dia; o ponto é a média.
        Faixa verde: faixa usual de adulto (glicemia: alvo de paciente internado).
      </p>
      {comGrafico.map((s) => {
        const dias = s.porDia!;
        const faixaUsual = faixaDoSinal(s.tipo);
        const g = geometriaDoSinal(dias, faixaUsual, L, H);
        const ultimo = dias[dias.length - 1]!;
        return (
          <div className="sinais-dia__linha" key={s.tipo}>
            <div className="sinais-dia__rotulo">
              <b>{ROTULO_SINAL[s.tipo] ?? s.tipo}</b>
              {faixaUsual && <small>faixa usual {numero(faixaUsual.min)}–{numero(faixaUsual.max)} {s.unidade}</small>}
            </div>
            <div className="sinais-dia__grafico">
              <svg viewBox={`0 0 ${L} ${H}`} preserveAspectRatio="none" role="img"
                aria-label={`${ROTULO_SINAL[s.tipo] ?? s.tipo}, ${dias.length} dias`}>
                {g.faixa && <rect x={0} y={g.faixa.y} width={L} height={g.faixa.h} className="sinais-dia__faixa" />}
                {g.barras.map((b, i) => <line key={i} x1={b.x} y1={b.y1} x2={b.x} y2={b.y2 + 0.01} className="sinais-dia__barra" />)}
                {g.pontos.length > 1 && <polyline points={g.pontos.map((p) => `${p.x},${p.y}`).join(' ')} className="sinais-dia__linha-media" />}
                {g.pontos.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={1.8} className="sinais-dia__ponto" />)}
              </svg>
              <div className="sinais-dia__eixo">
                <span>{formatarDiaMes(dias[0]!.dia)}</span>
                <span>{dias.length} dias</span>
                <span>{formatarDiaMes(ultimo.dia)}</span>
              </div>
            </div>
            <div className="sinais-dia__ultimo">
              {formatarDiaMes(ultimo.dia)}: média <b>{numero(ultimo.media)}</b>
              <small>de {numero(ultimo.menor)} a {numero(ultimo.maior)}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface FolhaProps {
  dados: RelatorioDTO;
  periodo: Periodo;
  paraFamilia: boolean;
  podeResponder: boolean;
  respondendo: string | null;
  textoResposta: string;
  quemRespondeu: string;
  salvando: boolean;
  aoResponder: (chave: string | null) => void;
  aoTrocarTexto: (v: string) => void;
  aoTrocarQuem: (v: string) => void;
  aoGuardar: (q: { chave: string; sobre: string; texto: string }) => void;
}

/**
 * O documento — só desenho, sem busca e sem estado próprio.
 *
 * Separado do `Relatorio` para que `ferramentas/conferir-relatorio.mts` possa
 * montar a folha com dados de verdade e gerar um HTML que se olha no navegador.
 * Foi assim que se descobriu, no plantão, um erro de fuso que nenhum teste
 * pegava — documento que a família entrega na mão do médico não se confere por
 * dedução.
 */
export function Folha({
  dados, periodo, paraFamilia, podeResponder, respondendo, textoResposta,
  quemRespondeu, salvando, aoResponder, aoTrocarTexto, aoTrocarQuem, aoGuardar,
}: FolhaProps) {
  const p = dados.paciente;
  const nome = identificar(p, !paraFamilia).nome;
  const setRespondendo = aoResponder;
  const setTextoResposta = aoTrocarTexto;
  const setQuemRespondeu = aoTrocarQuem;
  const marcarRespondida = aoGuardar;
  return (
        <article className="doc">
          {/* Timbre e rodapé só existem no papel, e aparecem em TODAS as
              páginas: `position: fixed` na impressão repete o elemento a cada
              folha. Sem isso, a partir da página 2 o documento vira uma pilha
              de tabelas soltas, sem dizer de quem é nem de quando. */}
          <div className="doc__timbre" aria-hidden="true">
            <span>
              {nome}
              {!paraFamilia && p.registro && ` · prontuário ${p.registro}`}
              {p.quarto && ` · quarto ${p.quarto}`}
            </span>
            <span>
              Relatório {PERIODOS.find((x) => x.chave === periodo)?.rotulo.toLowerCase()}
              {' · '}{dia(new Date(dados.gerado.em))}
            </span>
          </div>
          <div className="doc__pe" aria-hidden="true">
            <span>
              Registro de acompanhamento feito pela família · não é o prontuário
              do hospital
            </span>
            <span>saudev1</span>
          </div>
          {/* ------------------------------------------------- cabeçalho */}
          <header className="doc__cabeca">
            <div>
              {/* Na versão do médico, o nome de registro vem primeiro: é por ele
                  que o caso é achado no sistema do hospital depois que o PDF
                  impresso sai da mão da família. A versão da família continua
                  com o apelido, que é como ela é chamada em casa. Na do médico o
                  apelido não aparece: ele identifica como o hospital (27/09). */}
              <h1>{nome}</h1>
              {!paraFamilia && p.nomeCompleto && (
                <p className="doc__quem fraco">
                  {[
                    p.nascimentoEm && `nascimento ${dia(p.nascimentoEm)}`,
                    p.registro && `prontuário ${p.registro}`,
                    p.atendimento && `atendimento ${p.atendimento}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
              <p className="doc__quem">
                {p.idade} anos
                {p.quarto && <> · quarto <b>{p.quarto}</b></>}
                {' · '}{p.diasInternada}º dia de internação
                {' · desde '}{dia(p.internadoDesde)}
              </p>
              {p.noSetorDesde && p.diasNoSetor !== null && (
                <p className="doc__quem fraco">
                  {p.quarto ? `no quarto ${p.quarto}` : 'neste setor'} desde{' '}
                  {dia(p.noSetorDesde)} — {p.diasNoSetor}{' '}
                  {p.diasNoSetor === 1 ? 'dia' : 'dias'}
                </p>
              )}
            </div>
            <div className="doc__selo">
              <span>{PERIODOS.find((x) => x.chave === periodo)?.rotulo}</span>
              <small>{dados.gerado.periodoRotulo}</small>
              <small>gerado em {diaHora(dados.gerado.em)}</small>
            </div>
          </header>

          {/* ---------------------------------------------- passagem de turno
              Quem entra no quarto precisa saber o que aconteceu enquanto não
              estava lá. Sem isso, a enfermeira pergunta e a pessoa não sabe
              responder — que era o que vinha acontecendo. */}
          {periodo === 'turno' && dados.gerado.turnoQueSai && (
            <section className="doc__turno">
              <h2>Passando o turno</h2>
              <p>
                Pela família:{' '}
                <b>{dados.gerado.turnoQueSai.familia.join(' e ') || 'não registrado'}</b>,
                desde {diaHora(dados.gerado.turnoQueSai.inicioEm)}
                {' '}({dados.gerado.turnoQueSai.horas}h de turno).
              </p>
              {dados.gerado.turnoQueSai.equipe.length > 0 && (
                <p>
                  Equipe do hospital no turno:{' '}
                  <b>{dados.gerado.turnoQueSai.equipe.join(' · ')}</b>.
                </p>
              )}
              <p className="fraco">
                O que está abaixo aconteceu neste turno. O que não estiver
                escrito aqui, pergunte a quem está saindo — antes de ele ir embora.
              </p>
            </section>
          )}

          {periodo === 'turno' && !dados.gerado.turnoQueSai && (
            <p className="doc__aviso doc__aviso--atencao">
              Nenhum turno aberto no sistema, então isto mostra as
              <b> últimas 12 horas</b>. Para a passagem ficar exata, registre o
              turno no monitor quando chegar ao quarto.
            </p>
          )}

          <p className="doc__aviso">
            Registro feito pela família — <b>não é o prontuário do hospital</b> e
            não substitui a avaliação da equipe.
            {paraFamilia && ' As explicações dizem o que cada medida é, em geral.'}
          </p>

          {/* ------------------------------------------------ resumo do caso */}
          {/* Abre o documento porque foi para isso que ele nasceu: médico que
              entra no quarto sem saber que ela tem marca-passo. O dado existia
              numa tabela da terceira página — verdadeiro e inútil. */}
          {dados.resumo.alertas.length > 0 && (
            <section className="doc__alerta">
              {dados.resumo.alertas.map((a, i) => (
                <div key={i} className={`doc__alerta__item doc__alerta__item--${a.gravidade}`}>
                  <p className="doc__alerta__titulo">{a.texto}</p>
                  <p className="doc__alerta__fonte">{a.dispositivo}</p>
                  {a.restricoes.length > 0 && (
                    <ul className="doc__alerta__lista">
                      {a.restricoes.map((r, j) => (
                        <li key={j}><b>{r.o}</b> — {r.porque}</li>
                      ))}
                    </ul>
                  )}
                  {a.liberado && <p className="doc__alerta__ok">{a.liberado}</p>}
                </div>
              ))}
            </section>
          )}

          {(dados.resumo.texto || dados.resumo.emUso.length > 0) && (
            <section className="doc__bloco">
              <h2>Resumo do caso</h2>
              {dados.resumo.texto && <p className="doc__resumo">{dados.resumo.texto}</p>}
              {/* O resumo é texto de gente e envelhece. Quando aconteceu coisa
                  que pesa depois dele, o documento diz — em vez de abrir com a
                  história de dias atrás como se fosse a de hoje. */}
              {dados.resumo.desatualizado ? (
                <div className="doc__resumo-velho">
                  <b>Este resumo precisa ser revisto.</b>{' '}
                  {dados.resumo.desatualizado.escritoEm
                    ? <>Foi escrito em {dia(dados.resumo.desatualizado.escritoEm)}. Depois disso:</>
                    : <>Motivo:</>}
                  <ul>
                    {dados.resumo.desatualizado.itens.map((x, i) => <li key={i}>{x}</li>)}
                    {dados.resumo.desatualizado.maisN > 0 && <li>e mais {dados.resumo.desatualizado.maisN}</li>}
                  </ul>
                </div>
              ) : dados.resumo.atualizadoEm && (
                <p className="doc__legenda">Resumo escrito em {dia(new Date(dados.resumo.atualizadoEm))}.</p>
              )}
              {dados.resumo.emUso.length > 0 && (
                <p className="doc__legenda">
                  <b>Instalado agora:</b>{' '}
                  {dados.resumo.emUso.map((d) =>
                    `${d.nome}${d.local ? ` (${d.local})` : ''} desde ${dia(d.desde)}`,
                  ).join('; ')}.
                </p>
              )}
              {dados.resumo.medicacoes.length > 0 && (
                <p className="doc__legenda">
                  <b>Em uso agora:</b>{' '}
                  {dados.resumo.medicacoes.map((m) =>
                    `${m.nome}${m.dose ? ` ${m.dose}` : ''}${m.via ? ` ${m.via}` : ''}`,
                  ).join('; ')}.
                </p>
              )}
            </section>
          )}

          {/* ------------------------------------------------ sinais vitais */}
          {dados.sinais.length > 0 && (
            <section className="doc__bloco">
              <h2>Sinais vitais <small>{dados.gerado.periodoRotulo}</small></h2>
              <div className="rolaX">
              <table className="doc__tabela">
                <thead>
                  <tr>
                    <th>Medida</th><th>Último</th><th>Menor</th><th>Maior</th>
                    <th>Média</th><th>Aferições</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.sinais.map((s) => (
                    <tr key={s.tipo}>
                      <td><b>{ROTULO_SINAL[s.tipo] ?? s.tipo}</b> <span className="un">{s.unidade}</span></td>
                      <td className="num forte">{numero(s.ultimo)}</td>
                      <td className="num">{numero(s.menor)}</td>
                      <td className="num">{numero(s.maior)}</td>
                      <td className="num">{numero(s.media)}</td>
                      <td className="num fraco">{s.quantas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <SinaisDiaADia sinais={dados.sinais} />
            </section>
          )}

          {/* --------------------------------------------------- laboratório */}
          {dados.laboratorio.length > 0 && (
            <section className="doc__bloco">
              <h2>
                Laboratório
                {dados.gerado.laboratorioDesde && <small>desde a última coleta, de {dia(dados.gerado.laboratorioDesde)}</small>}
              </h2>
              <div className="rolaX">
              <table className="doc__tabela">
                <thead>
                  <tr>
                    <th>Exame</th><th>Último</th><th>Anterior</th>
                    <th>Tendência</th><th>Referência</th><th>Coletado</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.laboratorio.map((l) => (
                    <tr key={l.chave}>
                      <td>
                        <b>{l.rotulo}</b>
                        <span className="grupo">{ROTULO_GRUPO[l.grupo as keyof typeof ROTULO_GRUPO] ?? ''}</span>
                      </td>
                      <td className={`num forte v--${l.situacao}`}>
                        {l.valorTexto} <span className="un">{l.unidade}</span>
                        {/* A palavra, não só a cor: o mesmo documento vai para
                            uma impressora preto e branco, e lá a cor some. */}
                        {(l.situacao === 'alto' || l.situacao === 'baixo') && (
                          <span className="fora">{l.situacao}</span>
                        )}
                      </td>
                      <td className="num fraco">{l.anteriorTexto ?? '—'}</td>
                      <td className="num">{l.direcao ? `${SETA[l.direcao]} ${l.direcao}` : '—'}</td>
                      <td className="num fraco">
                        {/* A referência usa as casas do próprio marcador: com
                            arredondamento de uma casa, o pH normal (7,35–7,45)
                            virava "7,4–7,5", que é outro intervalo. */}
                        {l.referencia
                          ? `${faixa(l.referencia.min)}–${faixa(l.referencia.max)}`
                          : '—'}
                      </td>
                      {/* `new Date` porque este `coletadoEm` é um INSTANTE, não um dia
                          puro: sem isto, coleta das 22h aparece no dia seguinte. */}
                      <td className="num fraco">{dia(new Date(l.coletadoEm))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="doc__legenda">
                Valores fora da faixa usual de adulto vêm marcados com <b>alto</b> ou
                <b> baixo</b> ao lado do número, e também em cor. É sinalização, não
                interpretação — quem interpreta é a equipe.
              </p>

              {paraFamilia && (
                <div className="doc__explicacoes">
                  {dados.laboratorio
                    /* Todos os alterados, não os oito primeiros: o corte
                       escondia justamente os valores mais distantes da faixa. */
                    .filter((l) => l.explicacao && l.situacao !== 'dentro')
                    .map((l) => (
                      <div className="doc__explica" key={l.chave}>
                        <b>
                          {l.rotulo}
                          {l.vezesForaDaFaixa && l.vezesForaDaFaixa >= 1.5 && (
                            <span className={`fora v--${l.situacao}`}>
                              {numero(l.vezesForaDaFaixa)}× {l.situacao === 'alto' ? 'acima do limite' : 'abaixo do mínimo'}
                            </span>
                          )}
                        </b>
                        <p>{l.explicacao}</p>
                        {l.pergunta && (
                          <p className="doc__explica__pergunta"><b>Para perguntar:</b> {l.pergunta}</p>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </section>
          )}

          {/* ------------------------------------------------------ culturas */}
          {dados.culturas.length > 0 && (
            <section className="doc__bloco">
              <h2>Culturas e antibiograma</h2>
              <ul className="doc__lista">
                {dados.culturas.map((c, i) => (
                  <li key={i} className={c.aguardando ? 'espera' : ''}>
                    <div className="doc__lista__linha">
                      <b>{c.material}</b>
                      <span className="fraco">coletada em {dia(c.coletadoEm)}</span>
                      {c.deOutroSetor && <span className="doc__tag doc__tag--outro">outro setor</span>}
                      {c.aguardando && <span className="doc__tag doc__tag--espera">aguardando</span>}
                    </div>
                    {c.resultado && <p className="doc__lista__texto">{c.resultado}</p>}
                    {c.sensibilidade && <p className="doc__lista__texto fraco">sensível a: {c.sensibilidade}</p>}
                    {paraFamilia && c.explicacao && <p className="doc__explica__inline">{c.explicacao}</p>}
                  </li>
                ))}
              </ul>
              {dados.gerado.legendaDeSetor && dados.culturas.some((c) => c.deOutroSetor) && (
                <p className="doc__legenda">{dados.gerado.legendaDeSetor}</p>
              )}
            </section>
          )}

          {/* ------------------------------------------ imagem e procedimentos */}
          {dados.procedimentos.length > 0 && (
            <section className="doc__bloco">
              <h2>Exames de imagem e procedimentos</h2>
              <ul className="doc__lista">
                {dados.procedimentos.map((x, i) => (
                  <li key={i} className={x.pendente ? 'espera' : ''}>
                    <div className="doc__lista__linha">
                      <b>{x.nome}</b>
                      <span className="fraco">
                        pedido em {dia(x.solicitadoEm)}
                        {x.realizadoEm && ` · feito em ${dia(x.realizadoEm)}`}
                      </span>
                      {x.deOutroSetor && <span className="doc__tag doc__tag--outro">outro setor</span>}
                      {x.semLaudo && <span className="doc__tag doc__tag--espera">sem laudo</span>}
                      {x.pendente && (
                        <span className="doc__tag doc__tag--espera">
                          {x.situacao} há {x.diasDesdeOPedido} {x.diasDesdeOPedido === 1 ? 'dia' : 'dias'}
                        </span>
                      )}
                    </div>
                    {x.resultado && <p className="doc__lista__texto">{x.resultado}</p>}
                    {x.observacao && <p className="doc__lista__texto fraco">{x.observacao}</p>}
                    {paraFamilia && x.explicacao && <p className="doc__explica__inline">{x.explicacao}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ---------------------------------------------------- medicações */}
          {dados.medicacoes.length > 0 && (
            <section className="doc__bloco">
              <h2>Medicações</h2>
              <div className="rolaX">
              <table className="doc__tabela">
                <thead>
                  <tr><th>Medicação</th><th>Dose e via</th><th>Início</th><th>Fim</th><th>Dias</th></tr>
                </thead>
                <tbody>
                  {dados.medicacoes.map((m, i) => (
                    <tr key={i} className={m.emUso ? 'ativa' : 'encerrada'}>
                      <td>
                        <b>{m.nome}</b>
                        <span className={`doc__tag${m.emUso ? '' : ' doc__tag--fora'}`}>
                          {m.emUso ? 'em uso' : 'encerrada'}
                        </span>
                        {/* Curso com data para acabar: o antibiótico de 15 dias
                            sabe o próprio fim desde o primeiro dia. A família só
                            descobre isso se alguém contar — aqui está contado. */}
                        {m.diaDoCurso && m.diasDoCurso && (
                          <span className="grupo">
                            dia {m.diaDoCurso} de {m.diasDoCurso}
                            {m.faltamDias !== null && m.faltamDias >= 0 && (
                              <> · termina em {dia(m.previsaoFimEm!)}
                                {m.faltamDias === 0 ? ' (hoje)' : ` (faltam ${m.faltamDias} ${m.faltamDias === 1 ? 'dia' : 'dias'})`}</>
                            )}
                          </span>
                        )}
                        {/* O sistema não encerra remédio sozinho — curso pode ser
                            estendido, e tirar da lista faz a conferência deixar de
                            reconhecer o que ela toma. Mas ficar calado deixa a lista
                            virar um monte de coisa que acabou. Então ele cobra. */}
                        {m.precisaConfirmar && (
                          <span className="doc__cobranca">{m.precisaConfirmar}</span>
                        )}
                      </td>
                      <td className="fraco">{[m.dose, m.via].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="num fraco">{dia(m.inicioEm)}</td>
                      <td className="num fraco">
                        {m.fimEm ? dia(m.fimEm) : m.previsaoFimEm ? <em className="fraco">{dia(m.previsaoFimEm)}</em> : '—'}
                      </td>
                      <td className="num">{m.dias}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* O que cada remédio é, e o exame de controle que ele pede. Um
                  aviso aqui tem prazo: "a última CPK é de 37 dias atrás". */}
              {paraFamilia && dados.medicacoes.some((m) => m.emUso && m.explicacao) && (
                <div className="doc__explicacoes">
                  {dados.medicacoes.filter((m) => m.emUso && m.explicacao).map((m, i) => (
                    <div className="doc__explica" key={i}>
                      <b>{m.nome}</b>
                      <p>{m.explicacao!.texto}</p>
                      {m.explicacao!.aviso && (
                        <p className="doc__explica__aviso">{m.explicacao!.aviso}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* --------------------------------------------------- dispositivos */}
          {dados.dispositivos.length > 0 && (
            <section className="doc__bloco">
              <h2>Dispositivos</h2>
              <div className="rolaX">
              <table className="doc__tabela">
                <thead>
                  <tr><th>Dispositivo</th><th>Local</th><th>Inserido</th><th>Retirado</th><th>Dias</th></tr>
                </thead>
                <tbody>
                  {dados.dispositivos.map((d, i) => (
                    <tr key={i} className={d.instalado ? 'ativa' : 'encerrada'}>
                      {/* Toda linha diz o próprio estado: linha sem etiqueta
                          não distinguia "saiu" de "faltou o dado". */}
                      <td>
                        <b>{d.nome}</b>
                        <span className={`doc__tag${d.instalado ? '' : ' doc__tag--fora'}`}>
                          {d.instalado ? 'instalado' : 'retirado'}
                        </span>
                      </td>
                      <td className="fraco">{d.local ?? '—'}</td>
                      <td className="num fraco">{dia(d.inseridoEm)}</td>
                      <td className="num fraco">{d.retiradoEm ? dia(d.retiradoEm) : '—'}</td>
                      <td className={`num${d.instalado && d.dias >= 14 ? ' v--alto' : ''}`}>{d.dias}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </section>
          )}

          {/* ----------------------------------------------- o que foi aplicado
              Responde uma pergunta que ninguem no quarto conseguia responder:
              "que horas foi o ultimo antibiotico?". Vem da conferencia pela
              foto, e traz o nome como estava NO ROTULO. */}
          {dados.aplicacoes.length > 0 && (
            <section className="doc__bloco">
              <h2>Medicação aplicada e conferida <small>{dados.gerado.periodoRotulo}</small></h2>
              <ul className="doc__lista doc__lista--tempo">
                {dados.aplicacoes.map((a, i) => (
                  <li key={i}>
                    <div className="doc__lista__linha">
                      <b className="doc__quando">{diaHora(a.ocorreuEm)}</b>
                      <span>{a.nome}{a.dose ? ` · ${a.dose}` : ''}</span>
                      {a.veredito !== 'confere' && a.veredito !== 'feito' && (
                        <span className="doc__marca doc__marca--atencao">
                          {a.veredito === 'pare' ? 'aplicado apesar do alerta' : 'aplicado sem confirmação completa'}
                        </span>
                      )}
                    </div>
                    <p className="doc__lista__texto fraco">
                      {/* "feito": quem estava no quarto viu ser dado — sem foto do rótulo. */}
                      {a.veredito === 'feito' ? `Visto ser dado por ${a.conferidoPor}.` : `Rótulo conferido por ${a.conferidoPor}.`}
                      {a.observacao ? ` ${a.observacao}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* -------------------------------------------- como ela está */}
          {dados.evolucao.observacoes.length > 0 && (
            <section className="doc__bloco">
              <h2>
                Como ela está, dia a dia
                <small>últimos {dados.evolucao.dias} dias</small>
              </h2>
              <p className="doc__legenda">
                Observação da família, feita todo dia na mesma hora, com as mesmas
                três perguntas. Não é escala clínica: um dia não diz nada, e é a
                coluna — a sequência — que mostra se está mudando.
              </p>
              {dados.evolucao.tendencia && (
                <p className="doc__lista__texto"><b>{dados.evolucao.tendencia}</b></p>
              )}
              <div className="rolaX">
                <table className="doc__tabela doc__evolucao">
                  <thead>
                    <tr>
                      <th>Dia</th>
                      {PERGUNTAS_DO_DIA.map((p) => <th key={p.chave} className="num">{p.curto}</th>)}
                      <th className="num">Sono</th>
                      <th>O que chamou atenção</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.evolucao.observacoes.map((o) => (
                      <tr key={o.dia}>
                        <td className="num">{formatarDiaMes(o.dia)}</td>
                        {PERGUNTAS_DO_DIA.map((p) => (
                          <td key={p.chave} className={`num marca marca--${o[p.chave]}`}
                            title={ROTULO_RESPOSTA[o[p.chave]]}>
                            {MARCA_RESPOSTA[o[p.chave]]}
                          </td>
                        ))}
                        <td className="num un">{sonoEmTexto(o) ?? '—'}</td>
                        <td>{o.observacao ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="doc__legenda">
                As três perguntas: {PERGUNTAS_DO_DIA.map((p) => p.pergunta.toLowerCase()).join(' · ')}
                {' '}<b>S</b> sim, <b>±</b> mais ou menos, <b>N</b> não.
              </p>
            </section>
          )}

          {/* ---------------------------------------- intercorrências */}
          {/* Depois dos dados, não antes: pedido do Ilton, em 25/09. Aberta
              logo abaixo do resumo, a troca de medicação de 18/09 ocupava a
              página 2 inteira de todo relatório da semana, e quem lia chegava
              aos exames na página 3. Continua no documento, no período dela. */}
          {dados.intercorrencias.length > 0 && (
            <section className="doc__bloco">
              <h2>
                Intercorrências
                <small>{dados.intercorrencias.length === 1 ? 'um registro' : `${dados.intercorrencias.length} registros`} · {dados.gerado.periodoRotulo}</small>
              </h2>
              <ul className="doc__lista doc__ocorrencias">
                {dados.intercorrencias.map((i) => (
                  <li key={i.id} className={`doc__ocorrencia doc__ocorrencia--${i.gravidade}`}>
                    <div className="doc__lista__linha">
                      <b>{i.titulo}</b>
                      <span className="fraco">{diaHora(i.ocorreuEm)}</span>
                      <span className={`doc__tag doc__tag--${i.gravidade === 'grave' ? 'espera' : 'outro'}`}>
                        {i.gravidade}
                      </span>
                      {i.desfecho === 'evitado' && <span className="doc__tag">interrompido a tempo</span>}
                      {i.desfecho === 'aconteceu' && <span className="doc__tag doc__tag--espera">chegou a acontecer</span>}
                    </div>
                    <p className="doc__lista__texto">{i.oQueAconteceu}</p>
                    {i.oQueEraEsperado && (
                      <p className="doc__lista__texto"><b>O que era para ser:</b> {i.oQueEraEsperado}</p>
                    )}
                    {i.risco && (
                      <p className="doc__explica__aviso"><b>Risco do que foi administrado:</b> {i.risco}</p>
                    )}
                    {i.oQueFoiFeito && (
                      <p className="doc__lista__texto"><b>O que foi feito:</b> {i.oQueFoiFeito}</p>
                    )}
                    <p className="doc__legenda">
                      {i.quemDetectou && <>Detectado por {i.quemDetectou}. </>}
                      {i.registradoPor && <>Registrado por {i.registradoPor}.</>}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* --------------------------------------- observações da família
              Só na passagem de turno: quem chega precisa ler o que quem sai viu.
              Nos outros períodos os relatos têm documento próprio ("Relatos"),
              para não empurrar os exames para longe de quem lê. */}
          {periodo === 'turno' && dados.anotacoes.length > 0 && (
            <section className="doc__bloco">
              <h2>Relatos do turno <small>{dados.gerado.periodoRotulo}</small></h2>
              <ul className="doc__lista doc__lista--tempo">
                {dados.anotacoes.map((a, i) => (
                  <li key={i}>
                    <div className="doc__lista__linha">
                      <b className="doc__quando">{diaHora(a.ocorreuEm)}</b>
                      <span className="fraco">{a.autor}</span>
                    </div>
                    <NotaFormatada texto={a.texto} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ---------------------------------- perguntas para a visita */}
          {paraFamilia && dados.perguntas.length > 0 && (
            <section className="doc__bloco doc__bloco--perguntas">
              <h2>
                Para perguntar na visita
                {p.quarto && <small>à equipe do quarto {p.quarto}</small>}
              </h2>
              <ol className="doc__perguntas">
                {dados.perguntas.map((q) => (
                  <li key={q.chave}>
                    {/* O assunto vem antes da pergunta: sem isso, "já saiu a
                        parcial dessa cultura?" não diz de QUAL cultura, e o
                        médico responde sobre outra. */}
                    <span className="doc__sobre">{q.sobre}</span>
                    {q.texto}
                    {podeResponder && (
                      respondendo === q.chave ? (
                        /* Escrever a resposta é opcional. Quem só quer tirar da
                           lista tira; quem ouviu a explicação do médico escreve,
                           e essa frase vale mais que a pergunta. */
                        <div className="doc__responder">
                          <textarea
                            value={textoResposta} rows={3} autoFocus
                            placeholder="O que responderam? (pode deixar em branco)"
                            onChange={(e) => setTextoResposta(e.target.value)}
                          />
                          <input
                            type="text" value={quemRespondeu}
                            placeholder="quem respondeu (opcional)"
                            onChange={(e) => setQuemRespondeu(e.target.value)}
                          />
                          <div className="doc__responder__acoes">
                            <button className="botao" disabled={salvando}
                              onClick={() => void marcarRespondida(q)}>
                              {salvando ? 'guardando…' : 'Guardar e tirar da lista'}
                            </button>
                            <button className="botao botao--fantasma" onClick={() => setRespondendo(null)}>
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button className="doc__respondida" onClick={() => {
                          setRespondendo(q.chave); setTextoResposta(''); setQuemRespondeu('');
                        }}>
                          já foi respondida
                        </button>
                      )
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* --------------------------------------- o que já foi respondido
              A resposta vale mais que a pergunta: "a heparina é para prevenir
              trombose, porque ela está muito deitada" some no caminho do
              corredor até em casa, e é o que a próxima pessoa do quarto
              precisa saber para não perguntar de novo. */}
          {paraFamilia && dados.respondidas.length > 0 && (
            <section className="doc__bloco">
              <h2>Já respondido pela equipe <small>{dados.gerado.periodoRotulo}</small></h2>
              <ul className="doc__lista">
                {dados.respondidas.map((r, i) => (
                  <li key={i}>
                    <p className="doc__respondida__pergunta">{r.pergunta}</p>
                    {r.resposta && <p className="doc__lista__texto">{r.resposta}</p>}
                    <p className="fraco doc__respondida__quem">
                      {r.quemRespondeu ? `${r.quemRespondeu} · ` : ''}
                      anotado por {r.registradaPor} em {diaHora(r.respondidaEm)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* O que ficou de fora das perguntas — e por quê. Não some da vista:
              a família precisa saber que existe e onde buscar a resposta. */}
          {paraFamilia && dados.deOutrosSetores.length > 0 && (
            <section className="doc__bloco">
              <h2>Pedido em outro setor</h2>
              <p className="doc__legenda">
                Isto foi pedido antes da transferência
                {p.quarto && <> para o quarto <b>{p.quarto}</b></>}, por outra
                equipe. A equipe de agora não acompanhou esses pedidos e não tem
                como respondê-los — o resultado fica no prontuário do hospital.
                Por isso não entrou na lista de perguntas acima:{' '}
                {dados.deOutrosSetores.join('; ')}.
              </p>
            </section>
          )}

          {/* -------------------------------------------------------- plantão */}
          {dados.plantoes.length > 0 && (
            <section className="doc__bloco">
              <h2>Equipe de plantão <small>{dados.gerado.periodoRotulo}</small></h2>
              <div className="rolaX">
              <table className="doc__tabela">
                <thead><tr><th>Turno</th><th>De</th><th>Até</th><th>Equipe</th></tr></thead>
                <tbody>
                  {dados.plantoes.map((t, i) => (
                    <tr key={i}>
                      <td><b>{t.turno}</b></td>
                      <td className="num fraco">{diaHora(t.inicioEm)}</td>
                      <td className="num fraco">{t.fimEm ? diaHora(t.fimEm) : 'agora'}</td>
                      <td>{t.equipe.join(' · ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </section>
          )}

          <footer className="doc__rodape">
            {dados.gerado.aviso && <p>{dados.gerado.aviso}</p>}
            <p className="fraco">
              saudev1 · registro de apoio da família · gerado em {diaHora(dados.gerado.em)}
            </p>
          </footer>
        </article>
  );
}
